<script lang="ts">
  import CategoryCard from '$components/category-card.svelte';
  import Link from '$components/ui/link.svelte';

  import { t } from '$lib/i18n';
  import { region } from '$lib/region';
  import { categories } from '$lib/stores/content';
  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';
  import { buildPath } from '$lib/utils/paths';

  import type {
    ConfigurationStoryblok,
    PageCtaStoryblok,
    PageStoryblok,
    RegionalFeaturedPageStoryblok,
    ShopPageGroupStoryblok,
    ShopPageStoryblok
  } from '$types/bloks';
  import type { ISbStoryData } from '@storyblok/js';

  export let configuration: ConfigurationStoryblok;

  const mainCategory = $categories.filter(
    (c) => c.uuid === configuration.navigation_main_category
  )[0] as ISbStoryData<ShopPageGroupStoryblok>;

  const mainCategorySubCategories = $categories
    .filter((c) => mainCategory.content.sub_collections.includes(c.uuid))
    .sort(
      (a, b) =>
        mainCategory.content.sub_collections.indexOf(a.uuid) -
          mainCategory.content.sub_collections.indexOf(b.uuid) || a.uuid.localeCompare(b.uuid)
    ) as ISbStoryData<ShopPageStoryblok>[];

  // TODO: Revert this "hack" to obey collections structure
  //
  // const otherCategories = (
  //   $categories.filter((c) => configuration.navigation_other_categories?.includes(c.uuid)) || []
  // ).sort(
  //   (a, b) =>
  //     (configuration.navigation_other_categories || []).indexOf(a.uuid) -
  //       (configuration.navigation_other_categories || []).indexOf(b.uuid) ||
  //     a.uuid.localeCompare(b.uuid)
  // ) as ISbStoryData<ShopPageStoryblok>[];
  const otherCategories = configuration.navigation_other_categories as ISbStoryData<
    ShopPageStoryblok | PageStoryblok
  >[];

  let regionalizedFeaturePage: PageCtaStoryblok | undefined;

  $: {
    const featuredPages = configuration.navigation_featured_page_regionalized;
    if (featuredPages && featuredPages.length && $region) {
      regionalizedFeaturePage = featuredPages.find((fp: RegionalFeaturedPageStoryblok) =>
        fp.regions?.includes($region)
      )?.feature_page?.[0];
    } else {
      regionalizedFeaturePage = undefined;
    }
  }
</script>

<div class="container relative mx-auto flex h-full gap-4 overflow-y-auto py-8">
  <ul class="flex h-[280px] w-full gap-2">
    <div class="flex h-[280px] flex-1 grow gap-2 [&>*]:flex-1">
      {#each mainCategorySubCategories as story}
        {#if story.content.cover_image?.filename}
          {@const { src, alt } = getImageAttributes(story.content.cover_image, {
            size: [512, 0]
          })}
          <CategoryCard
            size="md"
            href={$buildPath({
              path: story.full_slug
            })}
            label={story.content.page_title || story.name}
            image={{ src, alt }}
          />
        {:else}
          <CategoryCard
            size="md"
            href={$buildPath({
              path: story.full_slug
            })}
            label={story.content.page_title || story.name}
          />
        {/if}
      {/each}
      <CategoryCard
        size="md"
        href={$buildPath({
          path: mainCategory.full_slug
        })}
        label={$t('topnav-all', { value: mainCategory.content.page_title || mainCategory.name })}
      />
    </div>
  </ul>

  <div class="min-w-[216px] max-h-[280px] relative flex flex-col items-start justify-start gap-2.5">
    <p class="text-sm font-semibold text-primary/40">{$t('topnav.shop-other')}</p>
    <ul class="flex flex-col items-start gap-2.5">
      {#each otherCategories as category}
        {@const href = $buildPath({
          path: category.full_slug
        })}
        <Link {href} class="text-lg font-semibold">
          {category.name}
        </Link>
      {/each}
    </ul>

    {#if regionalizedFeaturePage && regionalizedFeaturePage.image.filename}
      {@const { src } = getImageAttributes(regionalizedFeaturePage.image, {
        size: [512, 0]
      })}
      {@const { href } = $getAnchorFromCmsLink(regionalizedFeaturePage.link)}
      {#if href}
        <a
          {href}
          class="w-full isolate h-full grid place-items-center rounded-sm bg-cover relative after:rounded-sm after:absolute after:inset-0 after:bg-primary/18 after:transition-colors hover:after:bg-primary/14 motion-reduce:after:transition-none"
          style="background-image: url({src}); background-position: center;"
        >
          <span class="text-lg z-10 text-center font-semibold text-highlight"
            >{regionalizedFeaturePage.name}</span
          >
        </a>
      {/if}
    {/if}
  </div>
</div>
